import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  SelectChangeEvent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAdminUsers } from '../../hooks/admin/useAdminUsers';
import {
  INK,
  FONTS,
  primaryBtnSx,
  ghostBtnSx,
  ledgerInputSx,
  ledgerSelectSx,
  fieldLabelSx,
  sectionEyebrowSx,
  sectionTitleSx,
} from '../../styles/ledger';
import { LedgerPage, PageHeader } from '../../components/admin/LedgerPrimitives';

const Section: React.FC<{ number: string; eyebrow: string; title: string; description?: string; children: React.ReactNode }> = ({
  number,
  eyebrow,
  title,
  description,
  children,
}) => (
  <Box sx={{ borderTop: `1px solid ${INK.hairline}`, pt: 4, pb: 1 }}>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 6 }}>
      <Box sx={{ flex: { md: '0 0 220px' }, minWidth: { md: 220 } }}>
        <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1.5 }}>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.accent, fontWeight: 600, letterSpacing: '0.04em' }}>
            § {number}
          </Typography>
          <Typography sx={sectionEyebrowSx}>{eyebrow}</Typography>
        </Stack>
        <Typography sx={{ ...sectionTitleSx, fontSize: 22, mb: description ? 1 : 0 }}>{title}</Typography>
        {description && (
          <Typography sx={{ fontFamily: FONTS.sans, fontSize: 12.5, color: INK.muted, lineHeight: 1.5 }}>
            {description}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Stack>
  </Box>
);

const AddUserPage: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    use_for: 'personal',
    language: 'english',
    company: '',
    role: '',
    password: '',
    password_confirmation: '',
    is_active: true,
    is_admin: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createUser, isCreating } = useAdminUsers();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
    }
  };

  const handleBooleanChange = (name: string, value: boolean) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (formData.password && formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (formData.password !== formData.password_confirmation)
      newErrors.password_confirmation = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setApiError(null);
    try {
      await createUser(formData);
      navigate('/admin/users');
    } catch (err: any) {
      if (err.response?.data?.errors) {
        const formatted: Record<string, string> = {};
        Object.keys(err.response.data.errors).forEach((k) => {
          formatted[k] = err.response.data.errors[k][0];
        });
        setErrors(formatted);
      } else {
        setApiError(err.response?.data?.message || 'An error occurred while creating the user');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const field = (label: string, name: keyof typeof formData, props: any = {}) => (
    <Box>
      <Typography sx={fieldLabelSx}>{label}</Typography>
      <TextField
        fullWidth
        size="small"
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
        error={!!errors[name]}
        helperText={errors[name]}
        sx={ledgerInputSx}
        {...props}
      />
    </Box>
  );

  return (
    <LedgerPage eyebrowFooter="Almaship · Users · New entry">
      <PageHeader
        eyebrow="Admin · Directory"
        title={
          <>
            New
            <Box component="span" sx={{ fontStyle: 'italic', color: INK.accent }}>
              {' '}user.
            </Box>
          </>
        }
        description="Create an account for a teammate or customer. Email and password are required."
        actions={
          <Button onClick={() => navigate('/admin/users')} startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>
            Back
          </Button>
        }
      />

      {apiError && (
        <Alert severity="error" sx={{ borderRadius: 0, mb: 3, fontFamily: FONTS.sans, border: `1px solid ${INK.inactive}` }}>
          {apiError}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Section number="01" eyebrow="Identity" title="Who is this?" description="Required: first/last name and email.">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {field('First name', 'first_name', { required: true })}
            {field('Last name', 'last_name', { required: true })}
            {field('Email', 'email', { type: 'email', required: true })}
            {field('Phone', 'phone')}
          </Box>
        </Section>

        <Section number="02" eyebrow="Credentials" title="Login & access" description="Minimum 8 characters; both fields must match.">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {field('Password', 'password', { type: 'password', required: true })}
            {field('Confirm password', 'password_confirmation', { type: 'password', required: true })}
          </Box>
        </Section>

        <Section number="03" eyebrow="Profile" title="Account details" description="Optional metadata.">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography sx={fieldLabelSx}>Use for</Typography>
              <FormControl fullWidth size="small">
                <Select name="use_for" value={formData.use_for} onChange={handleSelectChange} sx={ledgerSelectSx}>
                  <MenuItem value="personal" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Personal</MenuItem>
                  <MenuItem value="business" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Business</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>Language</Typography>
              <FormControl fullWidth size="small">
                <Select name="language" value={formData.language} onChange={handleSelectChange} sx={ledgerSelectSx}>
                  {['english', 'arabic', 'spanish', 'french', 'german'].map((l) => (
                    <MenuItem key={l} value={l} sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>
                      {l.charAt(0).toUpperCase() + l.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {field('Company', 'company')}
            {field('Role', 'role', { placeholder: 'Manager, Developer, etc.' })}
          </Box>
        </Section>

        <Section number="04" eyebrow="Status" title="Activation & permission" description="Define whether this account starts active and elevated.">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography sx={fieldLabelSx}>Status</Typography>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ display: 'none' }}>Status</InputLabel>
                <Select
                  value={formData.is_active ? 'active' : 'inactive'}
                  onChange={(e) => handleBooleanChange('is_active', e.target.value === 'active')}
                  sx={ledgerSelectSx}
                >
                  <MenuItem value="active" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Active</MenuItem>
                  <MenuItem value="inactive" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>Role type</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.is_admin ? 'admin' : 'user'}
                  onChange={(e) => handleBooleanChange('is_admin', e.target.value === 'admin')}
                  sx={ledgerSelectSx}
                >
                  <MenuItem value="user" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>User</MenuItem>
                  <MenuItem value="admin" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Admin</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Section>

        <Box sx={{ borderTop: `1px solid ${INK.hairline}`, mt: 4, pt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, color: INK.subtle, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Draft · 4 sections
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button onClick={() => navigate('/admin/users')} sx={ghostBtnSx}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || isCreating}
              sx={{ ...primaryBtnSx, px: 3, py: 1.25 }}
              startIcon={isSubmitting || isCreating ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            >
              {isSubmitting || isCreating ? 'Creating…' : 'Create user'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </LedgerPage>
  );
};

export default AddUserPage;
